import { z } from 'zod';

/**
 * Frontend env validation.
 *
 * Only VITE_* variables are exposed to the browser bundle. We validate at
 * module load time so missing/invalid env crashes the build, not the user.
 *
 * Nothing user-facing is hardcoded. Everything UI-related (branding, theme,
 * features) comes from the AppConfig response, not from here.
 */

/**
 * VITE_API_BASE_URL accepts either:
 *   - A full URL: `http://localhost:3000/api/v1` (local dev pointing at the API directly)
 *   - A same-origin path: `/api/v1` (production - nginx on the host proxies /api/ to the API container)
 *
 * Axios resolves relative baseURLs against window.location.origin, so the
 * deployed web container stays environment-agnostic.
 */
const ApiBaseUrlSchema = z.string().refine((v) => v.startsWith('/') || /^https?:\/\//.test(v), {
  message: "Must be a full URL (https://...) or a same-origin path starting with '/'",
});

const EnvSchema = z.object({
  VITE_API_BASE_URL: ApiBaseUrlSchema,
  VITE_APP_BOOT_TIMEOUT_MS: z.coerce.number().int().positive().default(10_000),
  // Sentry. DSN empty/unset = telemetry disabled at build time.
  // RELEASE is the git SHA, baked in by CI so events group by deploy.
  // TRACES_SAMPLE_RATE 0..1: keep low to fit the free-tier quota.
  VITE_SENTRY_DSN: z.string().url().optional().or(z.literal('')),
  VITE_SENTRY_RELEASE: z.string().optional().or(z.literal('')),
  VITE_SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).default(0),
  VITE_ENVIRONMENT: z.enum(['development', 'staging', 'production']).default('development'),
  /**
   * Which product this build is: the full CRM (utazzocrm.com) or the
   * accounts/invoicing "Services" app (utazzoservices.com). Baked at build time
   * (Vite inlines VITE_*), so each domain ships its own image from the same
   * codebase. `crm` hides the invoicing surface; `services` restricts the whole
   * app to the FINANCE accounts surface. Defaults to `crm`.
   */
  VITE_APP_VARIANT: z.enum(['crm', 'services']).default('crm'),
  // Set by vite.config.ts on every build and written to /version.json; the
  // installed app compares the two to offer "new version - reload".
  VITE_APP_BUILD_ID: z.string().optional(),
  VITE_ENABLE_MOCKS: z
    .string()
    .optional()
    .default('false')
    .transform((v) => v.toLowerCase() === 'true'),
});

const parsed = EnvSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  • ${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('\n');

  console.error(`Invalid frontend env. Failing keys:\n${issues}\n\nReceived:`, import.meta.env);
  throw new Error(`Invalid frontend env:\n${issues}`);
}

export const env = parsed.data;
export type Env = z.infer<typeof EnvSchema>;
